import { BookText, ChevronsUpDown, CircleUser, LogOut, MonitorCog, Moon, Sun } from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import { PUBLIC_ROUTE } from '@/app/router/paths'
import { SEGMENTS } from '@/app/router/routes'
import { useCurrentAccountIdentity } from '@/features/account/hooks/use-current-account-identity'
import useLogout from '@/features/auth/hooks/use-logout'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/shared/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/shared/ui/sidebar'
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group'
import { useTheme } from '../theme-provider'

export const NavUser = () => {
  const navigate = useNavigate()
  const logout = useLogout()
  const { setTheme, theme } = useTheme()
  const { isMobile } = useSidebar()

  const signOut = async () => {
    await logout()
    navigate(`/${PUBLIC_ROUTE.AUTH}/${SEGMENTS.auth.login}`)
  }

  const account = useCurrentAccountIdentity()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar seed={account.seed} className="h-8 w-8 rounded-lg">
                {account.avatar ? (
                  <AvatarImage src={account.avatar} alt={account.fullName} />
                ) : null}
                <AvatarFallback className="rounded-lg">{account.fullName?.[0]}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{account.fullName}</span>
                <span className="truncate text-xs">{account.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar seed={account.seed} className="h-8 w-8 rounded-lg">
                  {account.avatar ? (
                    <AvatarImage src={account.avatar} alt={account.fullName} />
                  ) : null}
                  <AvatarFallback className="rounded-lg">{account.fullName?.[0]}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{account.fullName}</span>
                  <span className="truncate text-xs">{account.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to={`${SEGMENTS.account.root}`}>
                  <CircleUser className="size-5 mr-0.5" />
                  Mi Perfil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`${SEGMENTS.admin.changelog}`}>
                  <BookText className="size-5 mr-0.5" />
                  Changelog
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="focus:bg-transparent">
              <div className="flex items-center justify-between w-full">
                Tema
                <ToggleGroup type="single" size="sm" value={theme} className="gap-0.5">
                  <ToggleGroupItem
                    value="light"
                    aria-label="Toggle light"
                    className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setTheme('light')
                    }}
                  >
                    <Sun className="h-4 w-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="dark"
                    aria-label="Toggle dark"
                    className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setTheme('dark')
                    }}
                  >
                    <Moon className="h-4 w-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="system"
                    aria-label="Toggle system"
                    className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setTheme('system')
                    }}
                  >
                    <MonitorCog className="h-4 w-4" />
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut}>
              <LogOut className="size-5 mr-0.5" />
              Cerrar Sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
